import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AccountList } from '../../account/account-list/account-list';
import { debounceTime, distinctUntilChanged, EMPTY, fromEvent, map, startWith, Subject, Subscription, switchMap, timer } from 'rxjs';
import { BuyContractService } from './buy-contract.service';
import { BuyContract, BuyContractRow, BuyContractStatus, BUY_CONTRACT_STATUS_OPTIONS, BuyContractStatsReport } from './buy-contract.type';
import { BuyContractFormDialog } from './buy-contract-form-dialog/buy-contract-form-dialog';
import { PatchBuyContractDialog } from './patch-buy-contract-dialog/patch-buy-contract-dialog';
import { BuyContractAllocationsDialog } from './buy-contract-allocations-dialog/buy-contract-allocations-dialog';
import { BuyContractInfoDialog } from './buy-contract-info-dialog/buy-contract-info-dialog';
import { SupplierInfoDialog } from '../../supplier/supplier-info-dialog/supplier-info-dialog';
import { AutoBuyDialog } from './auto-buy-dialog/auto-buy-dialog';
import { TupleReportsDialog } from './tuple-reports-dialog/tuple-reports-dialog';
import { BillingEditDialog } from '../billing-edit-dialog/billing-edit-dialog';
import { ConfirmDialog } from '../../shared/confirm-dialog/confirm-dialog';
import { Header } from '../../shared/header/header';
import { ContractBalanceWidget } from '../contract-balance/contract-balance';
import { ContractPageNav } from '../contract-page-nav/contract-page-nav';
import { ContractBalanceService } from '../contract-balance/contract-balance.service';
import { ShareLinkDialog } from '../../shared/components/share-link-dialog/share-link-dialog';
import { AuthService } from '../../auth/auth.service';
import { UsersService } from '../../users/users.service';
import { UserRole } from '../../auth/auth.type';

@Component({
  selector: 'app-buy-contract',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    MatInputModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSidenavModule,
    AccountList,
    Header,
    ContractBalanceWidget,
    ContractPageNav,
  ],
  templateUrl: './buy-contract.html',
  styleUrl: './buy-contract.scss'
})
export class BuyContractPage implements OnInit {
  baseColumns = ['select', 'id', 'note', 'status', 'progress', 'billing', 'allocations_summary', 'timing', 'action'];

  contracts: BuyContractRow[] = [];
  loading = true;
  refreshing = false;
  private hasLoadedOnce = false;
  pageIndex = 0;
  pageSize = 20;
  total = 0;
  totalPages = 0;
  lastUpdate = 0;
  /** Bumped on every reload so the page nav refreshes its counts alongside the list. */
  refreshTick = 0;
  /** Tablet/mobile only: the non-search filters stay folded behind a toggle. */
  filtersOpen = false;
  isMobile = window.innerWidth <= 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  filterStatus: string = '';
  filterSearch: string = '';
  filterArchived: 'false' | 'true' | '' = 'false';
  filterOwnerRole: string = '';
  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;
  statusOptions: BuyContractStatus[] = BUY_CONTRACT_STATUS_OPTIONS;
  roleOptions: UserRole[] = [];

  /** Inline, user-fixable validation errors from the backend (400s) — shown next to the relevant filter instead of a toast. */
  searchError: string | null = null;
  dateRangeError: string | null = null;

  stats: BuyContractStatsReport | null = null;
  statsLoading = false;

  /** Admins default to their own normal view; they can opt into the admin panel view. */
  viewMode: 'normal' | 'admin' = 'admin';

  selectedIds = new Set<string>();
  archivingInactive = false;

  private searchSubject = new Subject<string>();
  private balanceService = inject(ContractBalanceService);
  /** Auto-buy state from the shared balance poll, so the header always shows whether the fleet is buying. */
  readonly autoBuy$ = this.balanceService.balance$.pipe(map(b => b?.autoBuy ?? null));
  private destroyRef = inject(DestroyRef);
  /** In-flight list/stats requests; a newer load cancels the older one so slow responses can't pile up. */
  private listSub?: Subscription;
  private statsSub?: Subscription;

  constructor(
    private service: BuyContractService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private usersService: UsersService,
  ) { }

  get isAdmin(): boolean {
    return this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  get canEdit(): boolean {
    return this.authService.canWrite('contracts');
  }

  get useAdminView(): boolean {
    return this.isAdmin && this.viewMode === 'admin';
  }

  get displayedColumns(): string[] {
    if (!this.useAdminView) return this.baseColumns;
    const cols = [...this.baseColumns];
    cols.splice(cols.indexOf('note'), 0, 'owner');
    return cols;
  }

  onViewModeChange(): void {
    this.pageIndex = 0;
    this.filterOwnerRole = '';
    this.loadContracts();
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.listSub?.unsubscribe();
      this.statsSub?.unsubscribe();
    });
    this.searchSubject.pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.searchError) return;
      this.pageIndex = 0;
      this.loadContracts();
    });
    if (this.isAdmin) {
      this.usersService.getAllRoles().subscribe({
        next: res => this.roleOptions = res.result,
        error: () => { /* role filter is optional; ignore failures */ },
      });
    }
    this.startAutoRefresh();
  }

  startAutoRefresh(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged()
    );

    // Both streams must stop with the page: left running, every visit stacked
    // another 30 s poll and 1 s change-detection tick until the tab crawled.
    visibility$.pipe(
      switchMap(visible => visible ? timer(0, 30_000) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.loadContracts());

    visibility$.pipe(
      switchMap(visible => visible ? timer(0, 1_000) : EMPTY),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => { this.lastUpdate++; });
  }

  onSearchChange(): void {
    // Quick client-side pre-check so obviously-broken regex gets immediate feedback
    // without waiting on a round trip; the backend (Postgres ~*) still has final say.
    if (this.filterSearch) {
      try {
        new RegExp(this.filterSearch);
        this.searchError = null;
      } catch {
        this.searchError = 'Invalid search pattern';
      }
    } else {
      this.searchError = null;
    }
    this.searchSubject.next(this.filterSearch);
  }

  /** date_from/date_to as inclusive RFC3339 bounds on created_at, shared by the list and stats-report calls. */
  private buildDateParams(): { date_from?: string; date_to?: string } {
    const params: { date_from?: string; date_to?: string } = {};
    if (this.filterStartDate) {
      params.date_from = this.filterStartDate.toISOString();
    }
    if (this.filterEndDate) {
      const endOfDay = new Date(this.filterEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      params.date_to = endOfDay.toISOString();
    }
    return params;
  }

  private extractInlineError(err: any): boolean {
    if (err?.status !== 400) return false;
    const message: string | undefined = err?.error?.error;
    if (!message) return false;
    if (/search/i.test(message)) {
      this.searchError = message;
    } else if (/date/i.test(message)) {
      this.dateRangeError = message;
    } else {
      return false;
    }
    return true;
  }

  loadContracts(): void {
    if (!this.hasLoadedOnce) {
      this.loading = true;
    }
    this.refreshing = true;
    this.refreshTick++;
    const archived = this.filterArchived === '' ? undefined : this.filterArchived === 'true';
    const dateParams = this.buildDateParams();
    const request$ = this.useAdminView
      ? this.service.adminGetBuyContracts({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        status: this.filterStatus as BuyContractStatus || undefined,
        search: this.filterSearch || undefined,
        ownerRole: this.filterOwnerRole || undefined,
        archived,
        ...dateParams,
      })
      : this.service.getBuyContracts({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        status: this.filterStatus as BuyContractStatus || undefined,
        search: this.filterSearch || undefined,
        archived,
        ...dateParams,
      });

    this.listSub?.unsubscribe();
    this.listSub = request$.subscribe({
      next: res => {
        this.contracts = res.items ?? [];
        this.total = res.total;
        this.totalPages = res.totalPages;
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
        this.lastUpdate = 0;
      },
      error: err => {
        if (!this.extractInlineError(err)) {
          this.snackBar.open('Error loading contracts', 'Close', { duration: 5000 });
        }
        this.loading = false;
        this.refreshing = false;
        this.hasLoadedOnce = true;
      }
    });
    this.loadStats();
  }

  loadStats(): void {
    this.statsLoading = true;
    const archived = this.filterArchived === '' ? undefined : this.filterArchived === 'true';
    this.statsSub?.unsubscribe();
    this.statsSub = this.service.getBuyContractStatsReport({
      search: this.filterSearch || undefined,
      archived,
      ...this.buildDateParams(),
    }).subscribe({
      next: res => {
        this.stats = res;
        this.statsLoading = false;
      },
      error: err => {
        this.extractInlineError(err);
        this.statsLoading = false;
      }
    });
  }

  onFilterChange(): void {
    this.dateRangeError = null;
    this.pageIndex = 0;
    this.loadContracts();
  }

  onDateRangeChange(): void {
    if (this.filterStartDate && this.filterEndDate && this.filterStartDate > this.filterEndDate) {
      this.dateRangeError = 'Start date must not be after end date';
      return;
    }
    this.onFilterChange();
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterSearch = '';
    this.filterArchived = 'false';
    this.filterOwnerRole = '';
    this.filterStartDate = null;
    this.filterEndDate = null;
    this.searchError = null;
    this.dateRangeError = null;
    this.pageIndex = 0;
    this.loadContracts();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadContracts();
  }

  setStatusFilter(status: string): void {
    this.filterStatus = status;
    this.onFilterChange();
  }

  setViewMode(mode: 'normal' | 'admin'): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.onViewModeChange();
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterStatus || this.filterSearch || this.filterArchived !== 'false'
      || this.filterOwnerRole || this.filterStartDate || this.filterEndDate);
  }

  /** Filters folded behind the toggle on small screens; search and status live outside it. */
  get hasExtraFilters(): boolean {
    return !!(this.filterArchived !== 'false' || this.filterOwnerRole || this.filterStartDate || this.filterEndDate);
  }

  openCreateContractDialog(): void {
    const isMobile = window.innerWidth <= 768;
    const ref = this.dialog.open(BuyContractFormDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
    });
    ref.afterClosed().subscribe(result => {
      if (result) this.loadContracts();
    });
  }

  viewAllocations(contract: BuyContract): void {
    this.dialog.open(BuyContractAllocationsDialog, {
      width: window.innerWidth <= 768 ? '100vw' : undefined,
      minWidth: window.innerWidth <= 768 ? undefined : '700px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { contractId: contract.id },
    });
  }

  shareLink(contract: BuyContract): void {
    const url = `${window.location.origin}/buy-contract/${contract.id}`;
    this.dialog.open(ShareLinkDialog, {
      data: { url },
      width: '420px',
      maxWidth: '95vw',
    });
  }

  openContractInfoDialog(contract: BuyContract): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(BuyContractInfoDialog, {
      data: { contract },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '520px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openSupplierInfoDialog(contract: BuyContractRow): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: contract.buyer_id, supplierUsername: contract.buyer_username },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openTupleReportsDialog(): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(TupleReportsDialog, {
      width: isMobile ? '100vw' : undefined,
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openAutoBuyDialog(): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(AutoBuyDialog, {
      width: isMobile ? '100vw' : '520px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    }).afterClosed().subscribe(() => this.balanceService.refresh());
  }

  openPatchDialog(contract: BuyContract): void {
    const isMobile = window.innerWidth <= 768;
    const ref = this.dialog.open(PatchBuyContractDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: '90vh',
      data: { contract },
    });
    ref.afterClosed().subscribe(result => {
      if (result) this.loadContracts();
    });
  }

  openBillingDialog(contract: BuyContract): void {
    const ref = this.dialog.open(BillingEditDialog, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        contractLabel: contract.name || this.shortId(contract.id),
        fee: contract.fee,
        payment_status: contract.payment_status,
      },
    });
    ref.afterClosed().subscribe(result => {
      if (!result) return;
      this.service.updateBuyContractBilling(contract.id, result).subscribe({
        next: () => {
          this.snackBar.open('Billing updated', 'Close', { duration: 4000 });
          this.loadContracts();
        },
        error: err => {
          const msg = err.error?.error || 'Error updating billing';
          this.snackBar.open(msg, 'Close', { duration: 5000 });
        }
      });
    });
  }

  cancel(contract: BuyContract): void {
    this.service.cancelBuyContract(contract.id).subscribe({
      next: () => {
        this.snackBar.open('Contract canceled', 'Close', { duration: 5000 });
        this.loadContracts();
      },
      error: () => {
        this.snackBar.open('Error cancelling contract', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  resume(contract: BuyContract): void {
    this.service.resumeBuyContract(contract.id).subscribe({
      next: () => {
        this.snackBar.open('Contract resumed', 'Close', { duration: 5000 });
        this.loadContracts();
      },
      error: () => {
        this.snackBar.open('Error resuming contract', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  releaseLocks(contract: BuyContract): void {
    this.service.releaseBuyContractLocks(contract.id).subscribe({
      next: () => {
        this.snackBar.open('Leftover locks released', 'Close', { duration: 5000 });
        this.loadContracts();
      },
      error: err => {
        const msg = err.error?.error || 'Error releasing locks';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  setArchived(contract: BuyContract, archived: boolean): void {
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: archived ? 'Archive Contract' : 'Unarchive Contract',
        message: archived
          ? `Are you sure you want to archive "${contract.name || this.shortId(contract.id)}"?`
          : `Are you sure you want to unarchive "${contract.name || this.shortId(contract.id)}"?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.archiveBuyContract(contract.id, archived).subscribe({
        next: () => {
          this.snackBar.open(archived ? 'Contract archived' : 'Contract unarchived', 'Close', { duration: 5000 });
          this.loadContracts();
        },
        error: () => this.snackBar.open('Error updating contract', 'Close', { duration: 5000 }),
      });
    });
  }

  bulkSetArchived(archived: boolean): void {
    const ids = [...this.selectedIds];
    if (!ids.length) return;
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: archived ? 'Archive Contracts' : 'Unarchive Contracts',
        message: archived
          ? `Are you sure you want to archive ${ids.length} contract(s)?`
          : `Are you sure you want to unarchive ${ids.length} contract(s)?`,
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.service.archiveBuyContracts(ids, archived).subscribe({
        next: () => {
          this.snackBar.open(archived ? `${ids.length} contract(s) archived` : `${ids.length} contract(s) unarchived`, 'Close', { duration: 5000 });
          this.selectedIds.clear();
          this.loadContracts();
        },
        error: () => this.snackBar.open('Error updating contracts', 'Close', { duration: 5000 }),
      });
    });
  }

  /**
   * Archives every non-archived, non-Active contract in one server-side call. Honors the
   * search / date / owner-role filters and the My/All scope, but ignores the status and
   * archived filters (the point is to sweep up everything that's finished).
   */
  archiveAllInactive(): void {
    const confirmRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Archive Inactive Contracts',
        message: 'Archive all Done, Canceled, Expired and Failed contracts that match the current search and date filters?',
      },
    });
    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.archivingInactive = true;
      const request = {
        search: this.filterSearch || undefined,
        ownerRole: this.useAdminView ? this.filterOwnerRole || undefined : undefined,
        ...this.buildDateParams(),
      };
      this.service.archiveInactiveBuyContracts(request, this.useAdminView).subscribe({
        next: res => {
          this.archivingInactive = false;
          this.snackBar.open(
            res.archived ? `${res.archived} contract(s) archived` : 'No inactive contracts to archive',
            'Close', { duration: 5000 });
          this.selectedIds.clear();
          this.loadContracts();
        },
        error: err => {
          this.archivingInactive = false;
          if (!this.extractInlineError(err)) {
            this.snackBar.open('Error archiving contracts', 'Close', { duration: 5000 });
          }
        },
      });
    });
  }

  toggleSelect(id: string): void {
    if (this.selectedIds.has(id)) this.selectedIds.delete(id);
    else this.selectedIds.add(id);
  }

  get allSelected(): boolean {
    return this.contracts.length > 0 && this.contracts.every(c => this.selectedIds.has(c.id));
  }

  get someSelected(): boolean {
    return this.selectedIds.size > 0 && !this.allSelected;
  }

  toggleSelectAll(): void {
    if (this.allSelected) this.contracts.forEach(c => this.selectedIds.delete(c.id));
    else this.contracts.forEach(c => this.selectedIds.add(c.id));
  }

  isActive(contract: BuyContract): boolean {
    return contract.status === 'Active';
  }

  isDone(contract: BuyContract): boolean {
    return contract.status === 'Done';
  }

  getStatusClass(status: BuyContractStatus): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Done': return 'status-done';
      case 'Canceled': return 'status-canceled';
      case 'Expired': return 'status-expired';
      case 'Failed': return 'status-failed';
    }
  }

  getPaymentClass(status: string): string {
    return status === 'Payed' ? 'payment-payed' : 'payment-not-payed';
  }

  getPaymentIcon(status: string): string {
    return status === 'Payed' ? 'check_circle' : 'cancel';
  }

  progressCoins(contract: BuyContract): number {
    return contract.target_type === 'received' ? contract.received_coins : contract.sent_coins;
  }

  progressPercent(contract: BuyContract): number {
    if (!contract.target_coins) return 0;
    return Math.min(100, Math.round((this.progressCoins(contract) / contract.target_coins) * 100));
  }

  formatCoins(n: number): string {
    if (!n) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  }

  formatToman(n: number): string {
    if (!n) return '—';
    return n.toLocaleString('en-US');
  }

  /** Status counts in a fixed, readable order; omits statuses with no matches. */
  get statsStatusEntries(): { status: BuyContractStatus; count: number }[] {
    if (!this.stats) return [];
    return this.statusOptions
      .map(status => ({ status, count: this.stats!.status_counts[status] ?? 0 }))
      .filter(e => e.count > 0);
  }

  /** Combined sent+received coin flow vs. total target, across all matched contracts regardless of status or target_type. */
  get statsPercentReached(): number {
    if (!this.stats || !this.stats.total_target) return 0;
    return Math.min(100, Math.round(((this.stats.total_sent) / this.stats.total_target) * 100));
  }

  formatFeeRate(rate: number): string {
    return (rate * 100).toFixed(2) + '%';
  }

  formatDateAndTime(d?: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString() + ' ' + new Date(d).toLocaleTimeString();
  }

  shortId(id: string): string {
    return id ? id.slice(0, 4) + '…' : '—';
  }

  trackByContractId(_index: number, c: BuyContractRow): string {
    return c.id;
  }
}
